/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IHelp, IOrderConfirmationViewProps, IPaymentMethods, IPriceContext, IGroup, IGroupDelivery,IGroups,
     IOrderInformation, ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { IOrderConfirmationProps, IOrderConfirmationResources } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import { PriceComponent } from '@msdyn365-commerce/components';
import { ChannelDeliveryOptionConfiguration, SalesLine } from '@msdyn365-commerce/retail-proxy';

interface ISummaryLineProps {
    priceContext?: IPriceContext;
    name: string;
    label: string;
    value?: number;
}

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
        <Node {...orderInformationProps}>
            {createdDate}
            {channelReferenceId}
            {receiptEmail}
        </Node>
    );

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
    </Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, processing, address, trackingInfo, pickupDateTimeslot }) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {delivery && delivery.shippingItemsToYou}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine:ISalesLine) => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group:IGroup, index:number) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

function priceContext(props: IOrderConfirmationViewProps): IPriceContext {
    const {
        id,
        typeName,
        context,
        telemetry,
        resources: { freePriceText }
    } = props;
    return {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    };
}

function shippingAmount(props: IOrderConfirmationViewProps): JSX.Element | null {
    const { request } = props.context;
    const ChargeAmount = props.data.orderHydration.result?.salesOrder.ChargeAmount;
    const ShippingChargeAmount = props.data.orderHydration.result?.salesOrder.ShippingChargeAmount;
    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const orderDetailsLines = props.data.orderHydration.result?.salesOrder.SalesLines;
    const { channelDeliveryOptionConfig, featureState } = props?.data;
    const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)?.IsEnabled;
    const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
    const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
    const canShip = orderDetailsLines?.some(orderDetailsLine => ((orderDetailsLine.DeliveryMode && orderDetailsLine.DeliveryMode !== '') ? (orderDetailsLine.DeliveryMode !== getDeliveryMode(orderDetailsLine, retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig?.result, pickupDeliveryModeCode) && orderDetailsLine.DeliveryMode !== emailDeliveryModeCode) : orderDetailsLine));
    return (
        canShip ? (
            <OrderSummaryLine
                name='shipping' label={props.resources.orderSummaryShippingFeeLabel} value={ShippingChargeAmount === 0 ? ChargeAmount : ShippingChargeAmount}
                priceContext={priceContext(props)} />
        ) : null
    );
}

function renderOtherChargeAmount(props: IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationResources>): JSX.Element | null {
    const salesOrder = props.data.orderHydration.result?.salesOrder;
    let totalOtherChargeAmount = 0;
     salesOrder!.SalesLines?.map((salesLine) => {
        const otherCharges = salesLine.ChargeLines?.filter((chargeline) => chargeline.ChargeCode === "OTHER");
        otherCharges && otherCharges.map((otherCharge, key) => {
            totalOtherChargeAmount = totalOtherChargeAmount + otherCharge.CalculatedAmount!;
        });
    });
    return (
        totalOtherChargeAmount ? (
            <OrderSummaryLine
                name='other-charge' label={props.resources.otherCharges} value={totalOtherChargeAmount}
                priceContext={priceContext(props)} />
        ) : null
    );
};

const getDeliveryMode = (salesLine: SalesLine, featureSate: boolean = false, channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration, pickupDeliveryMode?: string) => {
    if (!featureSate) {
        return pickupDeliveryMode;
    }
    return channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === salesLine.DeliveryMode);
};
const OrderSummaryLine: React.SFC<ISummaryLineProps> = ({ name, label, value = 0, priceContext }) => (
    <p className={`ms-order-confirmation__order-summary-line-${name}`}>
        <span className='ms-order-confirmation__order-summary-label'>
            {label}
        </span>
        {priceContext ? (
            <PriceComponent
                {...priceContext}
                className='ms-order-confirmation__order-summary-price'
                data={{ price: { CustomerContextualPrice: value } }}
            />
        ) : (
            <span className='ms-order-confirmation__order-summary-price'>
                {value}
            </span>
        )}
    </p>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationResources>> = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationResources>) => {
    const { orderSummaryProps, heading, subtotal, tax, totalAmount, earnedPoints } = props.orderSummary!;
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {shippingAmount(props)}
            {renderOtherChargeAmount(props)}
            {tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
}

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationResources>> = props => {
    const {
        moduleProps,
        heading,
        backToShoppingLink,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups
    } = props;
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} />}
            {payment && <OrderConfirmationPayment {...payment} />}
            {orderSummary && <OrderConfirmationOrderSummary {...props} />}
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
